import { http } from '@/lib/apiClient';
import type {
  CreateMacchinaInput,
  MacchinaConRelazioni,
  MacchinaFilters,
  MachineAlertsFilters,
  MachineAlertsResponse,
  MacchinaServizioStato,
  Paginated,
  UUID,
  UpdateMacchinaInput,
} from '@/types';

const BASE = '/macchine';

export const macchineService = {
  list(filters: MacchinaFilters = {}): Promise<Paginated<MacchinaConRelazioni>> {
    return http.get<Paginated<MacchinaConRelazioni>>(BASE, { params: filters });
  },

  get(id: UUID): Promise<MacchinaConRelazioni> {
    return http.get<MacchinaConRelazioni>(`${BASE}/${id}`);
  },

  create(input: CreateMacchinaInput): Promise<MacchinaConRelazioni> {
    return http.post<MacchinaConRelazioni, CreateMacchinaInput>(BASE, input);
  },

  update(id: UUID, input: UpdateMacchinaInput): Promise<MacchinaConRelazioni> {
    return http.patch<MacchinaConRelazioni, UpdateMacchinaInput>(`${BASE}/${id}`, input);
  },

  remove(id: UUID): Promise<void> {
    return http.delete(`${BASE}/${id}`);
  },

  /**
   * Current service health per machine (`macchina_servizio_stato`).
   * `modulo` narrows the result to one service family for module pages.
   */
  listStati(params: { clienteId?: UUID; modulo?: string; macchinaId?: UUID } = {}): Promise<MacchinaServizioStato[]> {
    return http.get<MacchinaServizioStato[]>('/servizi/stati', { params });
  },

  alerts(filters: MachineAlertsFilters = {}): Promise<MachineAlertsResponse> {
    return http.get<MachineAlertsResponse>(`${BASE}/alerts`, { params: filters });
  },

  getStati(macchinaId: UUID): Promise<MacchinaServizioStato[]> {
    return http.get<MacchinaServizioStato[]>(`${BASE}/${macchinaId}/stati`);
  },

  /**
   * Attach/detach go through `POST/DELETE /contratti/:id/macchine` — there is no
   * dedicated `/macchine/:id/contratti` route. The frontend wraps it so machine
   * screens can call `macchineService` without knowing the join-table owner.
   */
  attachContratto(macchinaId: UUID, contrattoId: UUID): Promise<void> {
    return http.post<void>(`/contratti/${contrattoId}/macchine`, { macchina_id: macchinaId });
  },

  detachContratto(macchinaId: UUID, contrattoId: UUID): Promise<void> {
    return http.delete(`/contratti/${contrattoId}/macchine/${macchinaId}`);
  },

  attachRiga(macchinaId: UUID, rigaId: UUID): Promise<void> {
    return http.post<void>(`${BASE}/${macchinaId}/contratti/${rigaId}`);
  },

  detachRiga(macchinaId: UUID, rigaId: UUID): Promise<void> {
    return http.delete(`${BASE}/${macchinaId}/contratti/${rigaId}`);
  },
};
